import React, { useMemo, useCallback, useState, useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useHistory } from "react-router-dom";

import {
  ManageDeletedData,
  ViewDamage,
  ViewDamageFromAssignedProject,
} from "../../../../services/authorization/Permissions";
import {
  ActionsSection,
  ButtonsSection,
  DataSection,
  SubSection,
} from "../../../../components/data-components/sections/DataSections";
import { DisplayDynamicProperties } from "../../../../components/data-components/DisplayDynamicProperties";
import {
  SectionMenuItemsModel,
  SectionDynamicMenuItemsPages,
} from "../../../sewer-objects/section-details/SectionMenuItems";
import PageLayout from "../../../_layout/PageLayout";
import EditButton from "../../../../components/buttons/edit-button/EditButton";
import DeleteButton from "../../../../components/buttons/delete-button/DeleteButton";
import DisplayImage from "../../../../components/data-components/DisplayImage";
import { CurrentDamageStorageModel } from "../../../../services/damage/CurrentDamageStorage";
import { CurrentSectionStorageModel } from "../../../../services/sewer-object/CurrentSectionStorage";
import useErrorHandler, {
  ApplicationError,
} from "../../../../services/error-handler/ErrorHandlerService";
import { Image as DamageImagePlaceholder } from "../../../../components/svg/svgImageList";
import DisplayDateCreated from "../../../../components/data-components/DisplayDateCreated";
import DisplayCreatedBy from "../../../../components/data-components/DisplayCreatedBy";
import {
  DisplayMainCode,
  DisplayCharacterization1,
  DisplayCharacterization2,
} from "../../damage-details/view-damage/components/DataComponents";
import { DynamicProperty } from "../../../../components/data-components/DynamicProperty";
import { CurrentInspectionStorageModel } from "../../../../services/inspection/CurrentInspectionStorage";
import { snackbarNotificationsService } from "../../../../services/notifications/SnackbarNotificationsService";
import useUrlParams from "../../../../services/url/UrlParamsService";
import { api } from "../../../../services/api/ApiService";
import { CollapsibleServiceModel } from "../../../../services/collapsible/CollapsibleService";
import { appSessionStorage } from "../../../../services/storage/SessionStorageService";
import GenericObjectIssues from "../../../../components/generic-object-issues/GenericObjectIssues";
import { permissionsService } from "../../../../services/authorization/UserPermissionsService";
import { navigatePath } from "../../../../helpers/StringHelpers";
import useRouteService from "../../../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../../../services/breadcrumbs/BreadcrumbsStorage";

export default observer(() => {
  const urlParamsService = useUrlParams();
  const history = useHistory();
  const { handleErrorInSnackbar } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const [currentSectionStorage] = useState(() =>
    CurrentSectionStorageModel.create({
      currentSectionId: urlParamsService.getSectionId()!,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    }, { api })
  );

  const [currentDamageStorage] = useState(() =>
    CurrentDamageStorageModel.create({ currentDamageId: urlParamsService.getDamageId()! }, { api })
  );

  const collapsibleService = CollapsibleServiceModel.create(
    { collapsedContentKey: "genericIssuesDamageIsCollapsed" },
    { appSessionStorage }
  );

  const currentInspectionId = urlParamsService.getInspectionId()!;

  const [currentInspectionStorage] = useState(() =>
    CurrentInspectionStorageModel.create({ currentInspectionId }, { api })
  );

  const menuItems = useMemo(() => {
    const sectionMenuItems = SectionMenuItemsModel.create({
      sectionId: currentSectionStorage.currentSectionId,
      section: currentSectionStorage.currentSection,
      inspectionId: currentInspectionId,
      damageId: currentDamageStorage.currentDamage.id,
      dynamicMenuItemPage: SectionDynamicMenuItemsPages.ViewDamage,
      parentIdFromPath: currentSectionStorage.parentEntityFromPath.id,
    });

    return {
      staticItems: sectionMenuItems.getStaticMenuItems(),
      dynamicItems: sectionMenuItems.getDynamicMenuItems(),
    };
  }, [currentSectionStorage.currentSectionId,
    currentSectionStorage.currentSection,
    currentSectionStorage.parentEntityFromPath.id,
    currentInspectionId,
    currentDamageStorage.currentDamage.id
  ]);

  const getCurrentInspectionName = useCallback(
    () =>
      currentInspectionStorage.currentInspection.getName(
        currentSectionStorage.currentSection.inspectionsWithDates
      ),
    [
      currentInspectionStorage.currentInspection,
      currentSectionStorage.currentSection.inspectionsWithDates,
    ]
  );

  const getDamageIsLockedWarning = useCallback(() => {
    if (
      currentDamageStorage.userWithLockOnDamage &&
      !currentDamageStorage.currentUserHasLockOnDamage
    )
      return {
        key: "ViewDamage.locked-inspection",
        context: { userName: currentDamageStorage.userWithLockOnDamage.name },
      };
    else return undefined;
  }, [currentDamageStorage.userWithLockOnDamage, currentDamageStorage.currentUserHasLockOnDamage]);

  const getFilteredDynamicProperties = useCallback((): DynamicProperty[] => {
    return currentDamageStorage.currentDamage.getPropertiesForEditNotInList([
      currentDamageStorage.currentDamage.mainCodeFieldIndicator,
      currentDamageStorage.currentDamage.c1CodeFieldIndicator,
      currentDamageStorage.currentDamage.c2CodeFieldIndicator,
    ]);
  }, [currentDamageStorage.currentDamage]);

  const mainCode: DynamicProperty | undefined = useMemo(() => {
    return currentDamageStorage.currentDamage.getDynamicPropertyByIndicator(
      currentDamageStorage.currentDamage.mainCodeFieldIndicator
    );
  }, [currentDamageStorage.currentDamage]);

  const c1Code: DynamicProperty | undefined = useMemo(() => {
    return currentDamageStorage.currentDamage.getDynamicPropertyByIndicator(
      currentDamageStorage.currentDamage.c1CodeFieldIndicator
    );
  }, [currentDamageStorage.currentDamage]);

  const c2Code: DynamicProperty | undefined = useMemo(() => {
    return currentDamageStorage.currentDamage.getDynamicPropertyByIndicator(
      currentDamageStorage.currentDamage.c2CodeFieldIndicator
    );
  }, [currentDamageStorage.currentDamage]);

  const handleEditClick = () => {
    history.push(
      `${history.location.pathname}/edit`
    );
  };
  const handleDeleteDamage = useCallback(async () => {
    try {
      await currentDamageStorage.deleteDamage();
      snackbarNotificationsService.showSuccess("ViewDamage.delete.success");
      history.push(
        `${navigatePath(history.location.pathname, -1)}`
      );
    } catch (error) {
      const applicationError = error as ApplicationError;
      applicationError.withErrorMessage("default-error.damage.delete");
      handleErrorInSnackbar(applicationError);
    }
  }, [currentDamageStorage, history, handleErrorInSnackbar]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
        entity: currentSectionStorage.parentEntityFromPath.type,
        name: await currentSectionStorage.getParentEntityName(currentSectionStorage.currentSection.project.name)
        }, {
        entity: EntityTypeFromPath.SewerObject,
        name: currentSectionStorage.currentSection.name
        }, {
          entity: EntityTypeFromPath.Inspection,
          name: getCurrentInspectionName()
        }, {
          entity: EntityTypeFromPath.Damage,
          name: currentDamageStorage.currentDamage.id.toString()
        }
      ]);

      breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentDamageStorage.currentDamage.id,
    currentSectionStorage,
    currentSectionStorage.currentSection,
    getCurrentInspectionName,
    history.location.pathname
  ]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewDamage, ViewDamageFromAssignedProject]}
      titleKey="ViewDamage.title"
      titleContext={{
        sectionName: currentSectionStorage.currentSection.name,
        inspectionName: getCurrentInspectionName(),
        damageId: currentDamageStorage.currentDamage.id,
      }}
      id="view-inspectionDamage"
      menuItems={menuItems}
      isLoading={currentDamageStorage.isLoading}
    >
      <ActionsSection
        warningKey={getDamageIsLockedWarning()?.key}
        warningContext={getDamageIsLockedWarning()?.context}
      >
        <ButtonsSection>
          <EditButton disabled={!currentDamageStorage.canSubmitEdit} onClick={handleEditClick} />
          <DeleteButton
            disabled={!currentDamageStorage.canUserDeleteDamage}
            confirmationAction={handleDeleteDamage}
            confirmationModalTitleKey="ViewDamage.delete.confirmation.title"
            confirmationModalSubtitleKey="ViewDamage.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <GenericObjectIssues
        collapsibleService={collapsibleService}
        genericIssues={currentDamageStorage.currentDamage.genericIssues}
      />
      <DataSection
        labelKey="ViewDamage.id"
        labelContext={{ damageId: currentDamageStorage.currentDamage.id }}
      >
        <SubSection className="image">
          {currentDamageStorage.damageHasImageUploaded ? (
            <DisplayImage
              id="damage-image"
              url={currentDamageStorage.imageUrl}
              className="damage"
            />
          ) : (
            <DisplayImage id="damage-image" placeholderImage={DamageImagePlaceholder} />
          )}
        </SubSection>
        <SubSection>
          <DisplayMainCode label={mainCode?.metadata.name} mainCode={mainCode} />
          <DisplayCharacterization1 label={c1Code?.metadata.name} characterization1={c1Code} />
          <DisplayCharacterization2 label={c2Code?.metadata.name} characterization2={c2Code} />
        </SubSection>
        <SubSection hideRightBorder>
          <DisplayDateCreated date={currentDamageStorage.currentDamage.created} />
          <DisplayCreatedBy createdBy={currentDamageStorage.currentDamage.createdBy.name} />
        </SubSection>
      </DataSection>
      <DisplayDynamicProperties
        labelKey="general.characteristics"
        properties={getFilteredDynamicProperties()}
      />
    </PageLayout>
  );
});
